import { HttpClient } from '@angular/common/http';
import { EnvironmentProviders, inject, provideAppInitializer, Provider } from '@angular/core';
import {
  InterpolatableTranslationObject,
  provideChildTranslateService,
  provideTranslateService,
  TranslateLoader,
  TranslateService,
  TranslationObject,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { forkJoin, map, Observable } from 'rxjs';

const TRANSLATION_FOLDER = 'i18n';

export const TranslationsSubfolder = {
  ACTIONS: 'actions',
  ERRORS: 'errors',
  ASSOCIATIONS: 'associations',
  COMMUNITIES: 'communities',
  AGENTS: 'agents',
  BUILDING: 'building',
  SITES: 'sites',
  FOLDERS: 'folders',
  SPENDINGS: 'spendings',
  PAYMENTS: 'payments',
  LOGIN: 'login',
  DASHBOARD: 'dashboard',
  MENU: 'menu',
  TABLE: 'table',
  OPERATOR: 'operator',
  OBJECT_DETAILS: 'object-details',
  FORM: 'form',
  UI: 'ui',
  ONBOARDING: 'onboarding',
  COMMENTS: 'comments',
  USER: 'user',
  NOT_FOUND: 'not-found',
} as const;

type TranslationsSubfolder = (typeof TranslationsSubfolder)[keyof typeof TranslationsSubfolder];

/**
 * Translations needed by any object-details view (action or folder), which can be opened — as a full
 * page or as a drawer — from anywhere. Loaded once at the authenticated layout so drawers inherit
 * them via the injector chain instead of each view re-loading its own.
 */
export const OBJECT_DETAILS_TRANSLATIONS = [
  TranslationsSubfolder.ACTIONS,
  TranslationsSubfolder.AGENTS,
  TranslationsSubfolder.ASSOCIATIONS,
  TranslationsSubfolder.FOLDERS,
  TranslationsSubfolder.FORM,
  TranslationsSubfolder.TABLE,
  TranslationsSubfolder.OBJECT_DETAILS,
  TranslationsSubfolder.OPERATOR,
  TranslationsSubfolder.COMMUNITIES,
  TranslationsSubfolder.COMMENTS,
  TranslationsSubfolder.USER,
] as const;

class MergeTranslateLoader implements TranslateLoader {
  private readonly _http = inject(HttpClient);

  constructor(private readonly subfolders: TranslationsSubfolder[]) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    return forkJoin(
      this.subfolders.map((subfolder) =>
        this._http.get<TranslationObject>(`${TRANSLATION_FOLDER}/${subfolder}/${lang}.json`),
      ),
    ).pipe(map((results) => Object.assign({}, ...results) as TranslationObject));
  }
}

export function provideTranslations(...subfolders: TranslationsSubfolder[]): Provider[] {
  if (subfolders.length === 1) {
    return provideChildTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: `${TRANSLATION_FOLDER}/${subfolders[0]}/`,
        suffix: '.json',
      }),
      extend: true,
    });
  }

  return provideChildTranslateService({
    loader: [
      {
        provide: TranslateLoader,
        useFactory: () => new MergeTranslateLoader(subfolders),
      },
    ],
    extend: true,
  });
}

/**
 * Route resolver that triggers translation loading for the current route's TranslateService.
 *
 * Any route whose `providers` include `provideTranslations(...)` MUST pair it with this
 * resolver. `provideTranslations` creates a *child* `TranslateService` scoped to that route's
 * injector, and a child service only loads its translations once `.use(lang)` is called on
 * that specific instance.
 */
export function loadTranslation(): Observable<InterpolatableTranslationObject> {
  return inject(TranslateService).use('fr');
}

export const NGX_TRANSLATE_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  provideTranslateService({
    fallbackLang: 'fr',
  }),
  provideAppInitializer(() => {
    const translate = inject(TranslateService);
    return translate.use('fr');
  }),

  provideTranslations(
    TranslationsSubfolder.MENU,
    TranslationsSubfolder.UI,
    TranslationsSubfolder.ONBOARDING,
    TranslationsSubfolder.ERRORS,
    TranslationsSubfolder.BUILDING,
  ),
];
